import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { ISelectOption, ISelectOptions } from '../select';
import LoopIcon from '@/assets/icons/loop.svg';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';

import PlusIcon from '@/assets/icons/plus-circle-green.svg';
import Chip from '../chip';

export type ComboBoxValue = Array<number | string>;

interface IProps extends Omit<AutocompleteProps<ISelectOption, true, false, false>, 'renderInput' | 'options' | 'value' | 'onChange'> {
    label?: string;
    placeholder?: string;
    options?: ISelectOptions;
    required?: boolean;
    value?: ComboBoxValue;
    wrapperClassName?: string;
    onChange?: (value: ComboBoxValue) => void;
}

const CustomIcon = styled('img')<{
    width?: string | number;
    height?: string | number;
}>(({ width, height }) => ({
    width: width || '26px',
    height: height || '26px'
}));

export const getConvertedValue = (value?: ComboBoxValue, options?: ISelectOptions) => {
    return options?.filter(item => (value || []).includes(item.value)) || [];
};

const AutoComplete = ({ label, required, options, placeholder, value, onChange, ...props }: IProps) => {
    const [selecteds, setSelecteds] = useState<ISelectOptions>(useMemo(() => getConvertedValue(value, options), [options, value]));

    const handleOnChange = (value: ISelectOptions) => {
        const newValue = value.map(item => item.value);
        onChange?.(newValue);
        setSelecteds(value);
    };

    return (
        <div className={`relative flex max-w-[480px] flex-col ${props.wrapperClassName ? props.wrapperClassName : 'min-w-56'}`}>
            {label && (
                <div className="font-atkinsonHyperlegible text-black sm:text-21">
                    {required && <span className="text-red-500">* </span>}
                    {label}
                </div>
            )}
            <Autocomplete
                multiple
                value={selecteds}
                onChange={(_, v) => handleOnChange(v)}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                options={options || []}
                getOptionLabel={option => option.label}
                disablePortal
                disableCloseOnSelect
                renderOption={(props, option) => (
                    <li
                        {...props}
                        key={JSON.stringify(option)}
                        className="flex cursor-pointer items-center justify-between bg-white px-2 py-1 font-dmSans hover:bg-mint-200 sm:px-4 sm:py-2"
                    >
                        {option.label}
                        <CustomIcon src={PlusIcon} width={18} height={18} />
                    </li>
                )}
                sx={{
                    padding: '0',
                    '& .MuiInputBase-root': {
                        padding: '10px 17px !important',
                        bgcolor: 'white',
                        boxShadow: '0px 1px 4px 0px #0000001A',
                        border: '0.5px solid #999999',
                        borderRadius: '8px',
                        '& .MuiChip-root': {
                            display: 'none'
                        },
                        '& .MuiInputBase-input': {
                            p: 0,
                            border: 'none',
                            ':hover': {
                                boxShadow: 'none'
                            }
                        },
                        fieldset: {
                            border: 'none'
                        }
                    }
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        inputProps={{
                            ...params.inputProps
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <CustomIcon src={LoopIcon} alt="loop icon" />
                                </InputAdornment>
                            )
                        }}
                    />
                )}
                componentsProps={{
                    popper: {
                        sx: {
                            width: '100% !important',
                            top: '1px !important'
                        }
                    },
                    paper: {
                        sx: {
                            boxShadow: '0px 4px 4px 2px #00000005'
                        }
                    }
                }}
                ListboxProps={{
                    sx: {
                        width: '100%',
                        boxShadow: '0px 4px 4px 2px #00000005',
                        border: '0.5px solid #999999',
                        borderTopWidth: 0,
                        borderRadius: '0 0 8px 8px',
                        padding: 0,

                        '& .MuiAutocomplete-option': {
                            bgcolor: 'white',
                            fontSize: '16px',
                            padding: '10px 20px',
                            '&:hover': {
                                bgcolor: 'primary.50'
                            }
                        }
                    }
                }}
                {...props}
            />
            <div className="flex flex-wrap gap-2 pt-2">
                {selecteds.map(option => (
                    <Chip
                        key={option.value}
                        label={option.label}
                        clearable
                        onDelete={() => handleOnChange(selecteds.filter(item => item.value !== option.value))}
                    />
                ))}
            </div>
        </div>
    );
};

export const ComboBox = AutoComplete;

export default AutoComplete;
